import {useDispatch, useSelector} from "react-redux";
import {useNavigate, useParams} from "react-router";
import React, {useContext, useEffect, useRef, useState} from "react";
import {getAllClientsById, getClientsAllIds, getClientsStatus, getCustomerClients} from "../../redux/CustomerClientsSlice";
import Grid from "@mui/material/Grid2";
import Button from "@mui/material/Button";
import {
    Alert, AlertTitle, Collapse, InputAdornment,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ProjectDetailRow from "../../Components/Projects/ProjectDetailRow";
import Box from "@mui/material/Box";
import {
    getQuoteById, getQuoteTemplates, getQuoteTemplatesStatus,
    putQuote, selectGetQuoteById, selectGetQuoteTemplatesById
} from "../../redux/ProjectsSlice";
import {ulid} from "ulid";
import NotFound from "../../Components/Error/NotFound";
import GeneralError from "../../Components/Error/GeneralError";
import {
    alphaNumericCodeEmptyValidator,
    alphaNumericValidator,
    checkAllValid
} from "../../Helpers/ValidationHelpers";
import ValidatedTextField from "../../Components/Inputs/ValidatedTextField";
import AutocompleteValidatedTextField from "../../Components/Inputs/AutocompleteValidatedTextField";
import {
    getAllProductsStatus, getProducts,
    selectGetAllProductGroupsAllIds,
    selectGetAllProductGroupsById, selectGetAllProductItemsAllIds,
    selectGetAllProductItemsById
} from "../../redux/ProductsSlice";
import TrimObjectStrings from "../../Helpers/TrimObjectStrings";
import {IsOnboardedContext} from "../../Contexts/IsOnboardedContext";
import {useTheme} from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import SaveIcon from "@mui/icons-material/Save";
import LoadingOverlay from "../../Components/Loading/LoadingOverlay";
import Tooltip from "@mui/material/Tooltip";
import InfoIcon from "@mui/icons-material/Info";
import RenderGoBackButton from "../../Helpers/RenderGoBackButton";
import equal from "fast-deep-equal";
import Breadcrumb from "../../Components/Navigation/Breadcrumb";
import LoadingWithStatusOverlay from "../../Components/Loading/LoadingWithStatusOverlay";
import ConfirmationDialog from "../../Components/Dialogs/ConfirmationDialog";
import TextField from "@mui/material/TextField";

function EditProject() {
    const { id } = useParams();
    const context = useContext(IsOnboardedContext);
    const theme = useTheme();
    const navigate = useNavigate();
    const [pathMap, setPathMap] = useState({});
    const dispatch = useDispatch();
    const quote = useSelector(selectGetQuoteById)[id]
    const [quoteError, setQuoteError] = useState(null);
    const [loadingStatus, setLoadingStatus] = useState("idle");
    const [loadingMessage, setLoadingMessage] = useState("");
    const [initialQuote, setInitialQuote] = useState(quote);
    const [activeForm, setActiveForm] = useState(quote);
    const [allQuoteDetailsSaved, setAllQuoteDetailsSaved] = useState({});
    const [updateComment, setUpdateComment] = useState('');
    
    useEffect(() => {
        document.title = 'Loading...'
        if (quote === undefined) {
            dispatch(getQuoteById(id)).unwrap()
                .then((response) => {
                    setInitialQuote(response);
                    setActiveForm(response);
                    document.title = `Edit ${response.friendlyId} - CPC`;
                    setPathMap({ [id]: response.friendlyId });
                })
                .catch((error) => {
                    setQuoteError(error);
                });
        } else {
            setInitialQuote(quote);
            setActiveForm(quote);
            document.title = `Edit ${quote.friendlyId} - CPC`;
            setPathMap({ [id]: quote.friendlyId });
        }
    }, [dispatch, quote, id]);
    
    useEffect(() => {
        if (activeForm) {
            activeForm?.quoteDetails?.forEach((detail) => {
                setAllQuoteDetailsSaved(prevState => ({
                    ...prevState,
                    [detail.tempId ?? detail.id]: true
                }))
            })
        }
    }, [activeForm])

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [addProductWarning, setAddProductWarning] = useState(false);

    const [invalidFormError, setInvalidFormError] = useState(false);
    const formValid = useRef({
        id: true,
        externalQuoteNumber: true,
        clientId: true,
        description: true,
    });

    const customerClients = useSelector(getAllClientsById);
    const clientIds = useSelector(getClientsAllIds);
    const clientsStatus = useSelector(getClientsStatus);

    const productGroups = useSelector(selectGetAllProductGroupsById);
    const productGroupIds = useSelector(selectGetAllProductGroupsAllIds);

    const productItems = useSelector(selectGetAllProductItemsById);
    const productItemIds = useSelector(selectGetAllProductItemsAllIds)
    const productsStatus = useSelector(getAllProductsStatus);

    const quoteTemplates = useSelector(selectGetQuoteTemplatesById);
    const quoteTemplatesStatus = useSelector(getQuoteTemplatesStatus);

    useEffect(() => {
        if (productsStatus === 'idle') {
            dispatch(getProducts());
        }
    }, [dispatch, productsStatus]);

    useEffect(() => {
        if (clientsStatus === 'idle') {
            dispatch(getCustomerClients());
        }
    }, [dispatch, clientsStatus]);

    useEffect(() => {
        if (quoteTemplatesStatus === 'idle') {
            dispatch(getQuoteTemplates());
        }
    }, [dispatch, quoteTemplatesStatus]);

    const customerClientOptions = clientIds.map(clientId => ({
            key: clientId,
            name: customerClients[clientId].name,
        })
    );

    const handleProductAddToQuote = () => {
        const newId = ulid();
        const newDetail = {
            tempId: newId,
            quoteDetailFieldValues: []
        };
        setActiveForm(prevState => ({
            ...prevState,
            quoteDetails: [...prevState.quoteDetails, newDetail]
        }));
        
        setAllQuoteDetailsSaved(prevState => ({
            ...prevState,
            [newId]: false
        }));
    };

    const handleDetailChange = (tempId, updatedDetail) => {
        setActiveForm(prevQuote => {
            const updatedQuoteDetails = prevQuote.quoteDetails.map(detail => {
                if (detail.tempId) {
                    return detail.tempId === tempId
                        ? updatedDetail
                        : detail
                }
                
                return detail.id === updatedDetail.id
                    ? updatedDetail
                    : detail
            });

            return { ...prevQuote, quoteDetails: updatedQuoteDetails };
        });
    };

    function handleSubmit(event) {
        event.preventDefault();

        if (equal(activeForm, initialQuote)) {
            setIsSubmitting(false);
            setInitialQuote(quote);
            setActiveForm(quote);
            navigate(`/projects/${id}`);
        }
    }

    const proceedWithSave = () => {
        setIsSubmitting(true);
        setInvalidFormError(false);
        setAddProductWarning(false);
        setLoadingStatus("loading");
        setLoadingMessage("Updating...");
        dispatch(putQuote(TrimObjectStrings(
            {
                id: activeForm.id,
                payload:
                    {
                        ...activeForm,
                        updatedBy: context.userName,
                        updatedByEmail: context?.user?.email,
                        comment: updateComment?.trim() || null,
                    }
            })
        )).unwrap()
            .then((response) => {
                setIsSubmitting(false);
                setInitialQuote(quote);
                setActiveForm(quote);
                setLoadingStatus("success");
                setLoadingMessage(`Project ${response.friendlyId} updated successfully!`);
            })
            .catch((error) => {
                console.error('Failed to update project', error);
                setIsSubmitting(false);
                const errorMessage = error?.status === 409
                    ? `Failed to update project. ${error?.detail}.`
                    : "Failed to update project. Please try again.";
                setLoadingStatus("error");
                setLoadingMessage(errorMessage);
            });
    };

    const handleConfirmSave = () => {
        if (activeForm.quoteDetails.length !== 0) {
            if (checkAllValid(formValid.current)) {
                proceedWithSave();
            } else {
                setInvalidFormError(true);
            }
        } else {
            setAddProductWarning(true);
        }
    };
    
    const handleCancelEdit = () => {
        setIsSubmitting(false);
        setInitialQuote(quote);
        setActiveForm(quote);
        navigate(`/projects/${id}`);
    }
    
    if (activeForm
        && initialQuote
        && quoteTemplatesStatus === 'succeeded'
        && productsStatus === 'succeeded'
        && clientsStatus === 'succeeded'
        && quoteError === null) {
        return (
            <>
                <Breadcrumb pathMap={pathMap} />
                <Box
                    component="form"
                    onSubmit={handleSubmit}
                    sx={{
                        '& .MuiTextField-root': { width: '40ch' },
                        "& .MuiInputBase-input": {
                            overflow: "hidden",
                            textOverflow: "ellipsis"
                        }
                    }}>
                    <Grid container spacing={2}>
                        <Grid size={12} sx={{ mb: 2 }}>
                            <Typography variant="h2">Edit {activeForm.friendlyId}</Typography>
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                            <Box
                                display="flex"
                                justifyContent={"left"}
                            >
                                <ValidatedTextField
                                    id="externalQuoteNumber"
                                    label="Enter Quote Number"
                                    size={"small"}
                                    validator={alphaNumericCodeEmptyValidator}
                                    onChange={isValid => (formValid.current.externalQuoteNumber = isValid)}
                                    setActiveForm={setActiveForm}
                                    isSubmitting={isSubmitting}
                                    initialValue={quote.externalQuoteNumber}
                                    removeHelperTextPadding
                                    fieldPath={['externalQuoteNumber']}
                                    infoAdornment={<InputAdornment position="end">
                                        <Tooltip enterTouchDelay={200} disableFocusListener title={"If you have a quote number from an external system, enter it here."}>
                                            <InfoIcon />
                                        </Tooltip>
                                    </InputAdornment>}
                                    width={{xs: '100%', md: '400px !important'}}
                                />
                            </Box>
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                            <Box
                                display="flex"
                                justifyContent={"left"}
                            >
                                <ValidatedTextField
                                    id="description"
                                    label="Description"
                                    size={"small"}
                                    validator={alphaNumericValidator}
                                    onChange={isValid => (formValid.current.description = isValid)}
                                    setActiveForm={setActiveForm}
                                    isSubmitting={isSubmitting}
                                    initialValue={activeForm.description}
                                    removeHelperTextPadding
                                    fieldPath={['description']}
                                    infoAdornment={null}
                                    required={true}
                                    width={{xs: '100%', md: '400px !important'}}
                                />
                            </Box>
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                            <Box
                                display="flex"
                                justifyContent={"left"}
                            >
                                <AutocompleteValidatedTextField
                                    isSubmitting={isSubmitting}
                                    setActiveForm={setActiveForm}
                                    label={"Client"}
                                    size={"small"}
                                    loadingStatus={clientsStatus}
                                    options={customerClientOptions}
                                    removeHelperTextPadding
                                    onChange={isValid => (formValid.current.clientId = isValid)}
                                    id={"clientId"}
                                    initialValue={customerClientOptions.find(option =>
                                            option.name === customerClients[activeForm.clientId]?.name
                                            || '')
                                        || null}
                                    infoAdornment={null}
                                    required
                                    width={{xs: '100%', md: '400px !important'}}
                                />
                            </Box>
                        </Grid>
                    </Grid>
                    <Grid container
                          spacing={2}
                    >
                        <Grid size={{ xs: 12 }}>
                            <TableContainer>
                                <Collapse in={activeForm.quoteDetails.length > 0} >
                                    <Table>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>
                                                    <Typography variant={"h4"}>Products</Typography>
                                                </TableCell>
                                            </TableRow>
                                        </TableHead>
                                            <TableBody>
                                                {activeForm.quoteDetails.map((detail) => (
                                                    <ProjectDetailRow
                                                        key={detail.tempId ?? detail.id}
                                                        quoteDetailId={detail.tempId ?? detail.id}
                                                        setActiveForm={setActiveForm}
                                                        isUpdate={!!detail.id}
                                                        activeQuoteDetail={detail}
                                                        handleDetailChange={handleDetailChange}
                                                        productGroups={productGroups}
                                                        productsStatus={productsStatus}
                                                        quoteTemplatesStatus={quoteTemplatesStatus}
                                                        productItems={productItems}
                                                        quoteTemplates={quoteTemplates}
                                                        productGroupIds={productGroupIds}
                                                        productItemIds={productItemIds}
                                                        isSubmitting={isSubmitting}
                                                        handleProductAddToQuote={handleProductAddToQuote}
                                                        allQuoteDetailsSaved={allQuoteDetailsSaved}
                                                        setAllQuoteDetailsSaved={setAllQuoteDetailsSaved}
                                                    />
                                                ))}
                                            </TableBody>
                                    </Table>
                                </Collapse>
                            </TableContainer>
                        </Grid>
                        <Grid size={{ xs: 12 }} sx={{my: 2}}>
                            <Button
                                variant="contained"
                                sx={{
                                    backgroundColor: theme.palette.tertiary.secondary
                                }}
                                startIcon={<AddIcon/>}
                                onClick={handleProductAddToQuote}
                            >
                                New Product
                            </Button>
                        </Grid>
                        {addProductWarning && activeForm.quoteDetails.length === 0 && 
                            <Grid size={{ xs: 12 }}>
                                <Alert severity="warning" >
                                    Please add at least one product to the quote.
                                </Alert>
                            </Grid>
                        }
                        {invalidFormError &&
                            <Grid size={{ xs: 12 }}>
                                <Alert severity="error" >
                                    <AlertTitle>Validation Error</AlertTitle>
                                    Please ensure all fields are correctly filled out.
                                </Alert>
                            </Grid>
                        }
                        <Grid size={{ xs: 12 }}>
                            <ConfirmationDialog
                                title="Update comment"
                                description="Would you like to add a comment for this update? This is optional and will be saved with your changes."
                                confirmButtonText="Save"
                                cancelButtonText="Cancel"
                                response={handleConfirmSave}
                                header="Save Changes"
                                content={
                                    <TextField
                                        fullWidth
                                        margin="normal"
                                        label="Update comment (optional)"
                                        value={updateComment}
                                        onChange={(e) => setUpdateComment(e.target.value)}
                                        placeholder=""
                                        
                                    />
                                }
                            >
                                {(showDialog) =>
                                    <>
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            onClick={() => {
                                                if (!equal(initialQuote, activeForm)) {
                                                    showDialog();
                                                } else {
                                                    setIsSubmitting(false);
                                                    setInitialQuote(quote);
                                                    setActiveForm(quote);
                                                    navigate(`/projects/${id}`);
                                                }
                                            }}
                                            startIcon={<SaveIcon/>}
                                            sx={{
                                                mr: 2
                                            }}
                                        >
                                            Save
                                        </Button>
                                        {RenderGoBackButton(equal(initialQuote, activeForm), handleCancelEdit)}
                                    </>}
                            </ConfirmationDialog>
                        </Grid>
                    </Grid>
                </Box>

                <LoadingWithStatusOverlay
                    status={loadingStatus}
                    message={loadingMessage}
                    successTimeout={1500}
                    failureTimeout={5000}
                    onComplete={() => {
                        if (loadingStatus === "success") {
                            navigate(`/projects/${id}`);
                        }
                        setLoadingStatus("idle");
                        setLoadingMessage("");
                    }}
                />
            </>
        );
    } else if (quoteError !== null) {
        if(quoteError.status === 404) {
            return (
                <div>
                    <NotFound />
                </div>
            );
        } else {
            return (
                <div>
                    <GeneralError />
                </div>
            )
        }
    } else {
        return <LoadingOverlay />
    }
}

export default EditProject;